import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, LogOut, LayoutDashboard, Menu, X, Bookmark } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES } from "../lib/categories";
import { getSiteMeta } from "../lib/meta";

export default function Nav({ onSearch }) {
  const { user, logout, openAuth } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [issue, setIssue] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    getSiteMeta().then((m) => setIssue(m.issue));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-obsidian/85 backdrop-blur-xl border-b border-white/10">
      <div data-testid="issue-bar" className="border-b border-white/5">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 h-7 flex items-center justify-between">
          <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-ash">
            Issue Nº {String(issue).padStart(3, "0")} <span className="text-kinetic">·</span> Daily Engine
          </span>
          <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-ash/50 hidden sm:block">The weekly, updated daily</span>
        </div>
      </div>
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0 group">
          <span className="w-9 h-9 bg-kinetic text-obsidian font-display font-extrabold text-sm flex items-center justify-center tracking-tight group-hover:rotate-[-6deg] transition-transform duration-300">
            M/M
          </span>
          <span className="font-display font-extrabold tracking-[0.18em] text-sm uppercase hidden sm:block">
            Modern Man
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          <NavLink to="/" end data-testid="nav-latest-link"
            className={({ isActive }) => `text-xs font-mono uppercase tracking-[0.2em] transition-colors ${isActive ? "text-kinetic" : "text-ash hover:text-porcelain"}`}>
            Latest
          </NavLink>
          <NavLink to="/voices" data-testid="nav-voices-link"
            className={({ isActive }) => `text-xs font-mono uppercase tracking-[0.2em] transition-colors ${isActive ? "text-kinetic" : "text-ash hover:text-porcelain"}`}>
            Voices
          </NavLink>
          <NavLink to="/lounge" data-testid="nav-lounge-link"
            className={({ isActive }) => `text-xs font-mono uppercase tracking-[0.2em] transition-colors ${isActive ? "text-kinetic" : "text-ash hover:text-porcelain"}`}>
            Lounge
          </NavLink>
          <NavLink to="/archive" data-testid="nav-archive-link"
            className={({ isActive }) => `text-xs font-mono uppercase tracking-[0.2em] transition-colors ${isActive ? "text-kinetic" : "text-ash hover:text-porcelain"}`}>
            Archive
          </NavLink>
          <NavLink to="/directory" data-testid="nav-directory-link"
            className={({ isActive }) => `text-xs font-mono uppercase tracking-[0.2em] transition-colors ${isActive ? "text-kinetic" : "text-ash hover:text-porcelain"}`}>
            Directory
          </NavLink>
          {user && user.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin-link"
              className={({ isActive }) => `text-xs font-mono uppercase tracking-[0.2em] transition-colors flex items-center gap-1.5 ${isActive ? "text-kinetic" : "text-ash hover:text-porcelain"}`}>
              <LayoutDashboard size={13} /> Studio
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <Link to="/reading-list" data-testid="nav-reading-list-link" title="Reading list"
            className="p-2.5 text-ash hover:text-kinetic border border-white/10 hover:border-kinetic/50 rounded-full transition-colors duration-300">
            <Bookmark size={14} />
          </Link>
          <button data-testid="nav-search-trigger-button" onClick={onSearch}
            className="flex items-center gap-2 text-ash hover:text-kinetic border border-white/10 hover:border-kinetic/50 rounded-full px-3.5 py-2 transition-colors duration-300">
            <Search size={14} />
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] hidden lg:block">Search ⌘K</span>
          </button>
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link to={`/member/${user.id}`} data-testid="nav-user-name" className="text-xs font-mono text-ash hover:text-kinetic max-w-[120px] truncate transition-colors">{user.name}</Link>
              <button data-testid="nav-logout-button" onClick={handleLogout} title="Sign out"
                className="p-2 text-ash hover:text-crimson border border-white/10 hover:border-crimson/50 rounded-full transition-colors duration-300">
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <button data-testid="nav-signin-button" onClick={() => openAuth("login")}
              className="hidden md:block bg-kinetic text-obsidian text-xs font-mono uppercase tracking-[0.15em] font-medium px-5 py-2.5 rounded-full hover:bg-porcelain transition-colors duration-300">
              Sign In
            </button>
          )}
          <button data-testid="nav-menu-button" onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 text-porcelain">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 flex gap-1 overflow-x-auto no-scrollbar">
          {CATEGORIES.map((c) => (
            <NavLink key={c.slug} to={`/category/${c.slug}`} data-testid={`nav-category-link-${c.slug}`}
              className={({ isActive }) => `whitespace-nowrap text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-2.5 transition-colors duration-300 ${isActive ? "text-kinetic" : "text-ash hover:text-porcelain"}`}>
              {c.label}
            </NavLink>
          ))}
        </div>
      </div>

      {menuOpen && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-white/10 bg-obsidian px-5 py-4 flex flex-col gap-4">
          <NavLink to="/" onClick={() => setMenuOpen(false)} className="text-sm font-mono uppercase tracking-[0.2em] text-porcelain">Latest</NavLink>
          <NavLink to="/voices" onClick={() => setMenuOpen(false)} className="text-sm font-mono uppercase tracking-[0.2em] text-porcelain">Voices</NavLink>
          <NavLink to="/lounge" onClick={() => setMenuOpen(false)} data-testid="nav-lounge-link-mobile" className="text-sm font-mono uppercase tracking-[0.2em] text-porcelain">Lounge</NavLink>
          <NavLink to="/archive" onClick={() => setMenuOpen(false)} className="text-sm font-mono uppercase tracking-[0.2em] text-porcelain">Archive</NavLink>
          <NavLink to="/directory" onClick={() => setMenuOpen(false)} className="text-sm font-mono uppercase tracking-[0.2em] text-porcelain">Directory</NavLink>
          {user && user.role === "admin" && (
            <NavLink to="/admin" onClick={() => setMenuOpen(false)} className="text-sm font-mono uppercase tracking-[0.2em] text-kinetic">Studio</NavLink>
          )}
          {user ? (
            <button onClick={() => { setMenuOpen(false); handleLogout(); }} className="text-left text-sm font-mono uppercase tracking-[0.2em] text-crimson">Sign Out ({user.name})</button>
          ) : (
            <button data-testid="nav-signin-button-mobile" onClick={() => { setMenuOpen(false); openAuth("login"); }} className="text-left text-sm font-mono uppercase tracking-[0.2em] text-kinetic">Sign In</button>
          )}
        </div>
      )}
    </header>
  );
}
